import api, { ApiResponse, isErrorHandled } from 'api';
import Button from 'components/button/Button';
import { TFAField } from 'components/form/TFAField';
import { Modal } from 'components/modal/Modal';
import { endpoints } from 'endpoints.config';
import { getErrorMessage } from 'errors';
import { Form, Formik, FormikHelpers } from 'formik';
import { toCamelCase } from 'helpers/formatFormFieldNames';
import { Toast } from 'helpers/toast';
import { useTFAField } from 'helpers/useTFAField';
import * as Yup from 'yup';
import { ManagedMerchant, merchantErrorMessage, MerchantCredentials } from './models';

export type MerchantSecretAction = 'reveal' | 'regenerate';

type Props = {
    merchant: ManagedMerchant;
    action: MerchantSecretAction;
    onClose: () => void;
    onSuccess: (credentials: MerchantCredentials) => void;
};

type FormState = {
    tfaCode: string;
};

const validationSchema = Yup.object({
    tfaCode: Yup.string().required('Please enter your two-factor authentication code'),
});

export const MerchantSecretModal = ({ merchant, action, onClose, onSuccess }: Props) => {
    const [tfaType, toggleTfaType, reSendTfaCode] = useTFAField();
    const regenerate = action === 'regenerate';

    const onSubmit = async ({ tfaCode }: FormState, helpers: FormikHelpers<FormState>) => {
        try {
            const res = await api.post<ApiResponse<MerchantCredentials>>(
                regenerate ? endpoints.merchantManagement.regenerateSecret : endpoints.merchantManagement.revealSecret,
                { widgetClientsId: merchant.widgetClientsId, tfaCode, tfaType }
            );
            if (res.data.status === '1' && res.data.details) {
                onSuccess({ ...res.data.details, apiKey: res.data.details.apiKey ?? merchant.apiKey });
                return;
            }
            Toast.openErrorToast(merchantErrorMessage(res.data.errors?.[0]?.messageCode));
        } catch (err: any) {
            if (isErrorHandled(err)) {
                const errors = err.response.data.errors;
                const tfaError = errors.find((error) => toCamelCase(error.fieldName) === 'tfaCode');
                if (tfaError) {
                    helpers.setFieldError('tfaCode', getErrorMessage(tfaError.messageCode));
                    return;
                }
                Toast.openErrorToast(merchantErrorMessage(errors[0]?.messageCode));
                return;
            }
            Toast.openErrorToast(merchantErrorMessage());
        }
    };

    return (
        <Formik initialValues={{ tfaCode: '' }} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ isValid, isSubmitting }) => (
                <Form>
                    <Modal
                        className="MerchantSecretModal"
                        title={regenerate ? 'Generate a new secret' : 'Show secret'}
                        onCloseModal={onClose}
                        closeOnClickOutside={false}
                        customButtons={
                            <div className="ModalNavigation">
                                <Button priority="secondary" type="button" onClick={onClose}>
                                    Cancel
                                </Button>
                                <Button priority="primary" type="submit" disabled={isSubmitting || !isValid}>
                                    {regenerate ? 'Generate new secret' : 'Show'}
                                </Button>
                            </div>
                        }
                    >
                        <>
                            {regenerate && (
                                <p className="MerchantSecretWarning">
                                    The current secret of{' '}
                                    <span className="notranslate" translate="no">
                                        {merchant.merchantName}
                                    </span>{' '}
                                    stops working straight away. The merchant must update its QuickBuy set up
                                    with the new secret.
                                </p>
                            )}
                            <TFAField
                                field="tfaCode"
                                label="TFA code"
                                required={true}
                                toggleTFAType={toggleTfaType}
                                tfaType={tfaType}
                                handleResendCode={reSendTfaCode}
                                autoFocus
                                holdFocus
                                onClose={onClose}
                                showTimer={true}
                            />
                        </>
                    </Modal>
                </Form>
            )}
        </Formik>
    );
};
